<template>
    <div class="d-flex flex-column" style="height: calc(100vh - 70px);">
        <div v-if="itemId">
            <inventory-form
                :key="itemFormKey"
                @closeItem="clearItemId()"
                @switchToTemplate="switchToTemplate($event)"
                @itemSaved="handleItemSaved($event)"
                :itemIdProp="this.itemId"
                :templateItemProp="templateItem"
            />
        </div>
        <div class="d-flex flex-column" style="height: calc(100vh - 70px);" v-else>
        <b-container fluid>
            <b-row>
                <b-col>
                    <inventory-search :filters="filters" :searchText="searchQuery.query" @search="doSearch" @clear="resetFilters" @showFilters="updateFiltersVisibility" @setFilters="setFilters" @removeFilter="this.removeFilter"></inventory-search>
                </b-col>
            </b-row>

            <b-row v-if="hasFilters && !showFilters" class="mb-3">
                <b-col>
                    <ul id="my-custom-tags-list"
                    class="list-unstyled d-inline-flex flex-wrap mb-0">
                    <span v-for="(value, filter, index) in filters" :key="index">
                    <b-card v-if="value && filter !== 'itemIds'" 
                        tag="li"
                        class="mt-1 mr-1"
                        body-class="py-1 pr-2 text-nowrap">
                            <strong v-if="filter === 'orderDate' || filter === 'orderDateTo'">{{ filterName(filter) }}{{ typeof value !== 'boolean' ? `: ${moment(value).format('M/DD/YYYY')}` : null }}</strong>
                            <strong v-else>{{ filterName(filter) }}{{ typeof value !== 'boolean' ? `: ${value}` : null }}</strong>
                            <b-button
                            @click="removeFilterItem(filter)" 
                            variant="link"
                            size="sm">x</b-button>
                    </b-card>
                    </span>
                    </ul>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <item-menu :selected="selected"
                                :rowCount="numItems"
                                :selectRange="selectRange"
                                :busy="creatingItem"
                                @selectAll="$refs.inventoryTable.selectAllRows()"
                                @clearSelected="$refs.inventoryTable.clearSelected()"
                                @printSelected="print()"
                                @addItem="addItem()"
                                @createTemplate="createTemplate()"
                                @updateSettings="settings = $event"
                                @setSelectRange="setSelectRange()">
                    </item-menu>
                </b-col>
            </b-row>
        </b-container>

        <b-container fluid class="flex-grow-1 p-0 m-0 overflow-auto">
            <b-table sticky-header="100vh" primary-key="item" selectable small no-border-collapse
                     class="h-100 m-0 pb-4"
                     :items="provider"
                     :fields="fields"
                     :per-page="perPage"
                     :current-page="currentPage"
                     @row-selected="rowSelected"
                     :busy.sync="isBusy"
                     :select-mode="(selectRange ? 'range' : 'multi')"
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     ref="inventoryTable"
                     id="inventoryTable"
                     caption-top>
                <template #cell(itemId)="row">
                    <b-link append @click="openItem(row.value)">{{ row.value }}</b-link>
                </template>

                <template #cell(artist)="data">
                    <span v-html="artistData(data)"></span>
                </template>

                <template #cell(title)="data">
                    <span v-html="titleData(data)"></span>
                </template>

                <template #cell(label)="data">
                    <span v-html="labelData(data)"></span>
                </template>

                <template #cell(genre)="data">
                    <ul class="p-0 d-flex flex-column">
                        <li class="cellListItem list-unstyled" v-for='(genre, index) in data.item.release.genres' :key='index'>
                        {{genre.name}}
                        </li>
                    </ul>
                </template>

                <template #cell(format)="data">
                    <span v-html="format(data)"></span>
                </template>

                <template #cell(pressing)="data">
                    <span class='d-flex' v-html="pressingData(data.item.release.pressing)"></span>
                </template>

                <template #cell(media)="data">
                    <span v-html="mediaData(data.item.release.media)"></span>
                </template>

                <template #cell(active)="data">
                    <span>{{ data.item.listing.count }}</span>

                    <span v-if="settings.showTagButtons" class="ml-2">
                        <b-button size="sm" variant="outline-secondary" @click="printTag(data.item.listing._id, data.item.listingIndex)">
                            <b-icon-tag></b-icon-tag>
                        </b-button>

                        <div v-if="barcodeItem === data.item.listing._id" :id="'listingTag-' + data.item.listing._id + '-' + data.item.listingIndex" class="barcode-container">
                            <barcode :data="barcodeData(data.item, data.item.listingIndex)"
                                     :text="barcodeData(data.item, data.item.listingIndex)"
                                     :location="data.item.listing.location"
                                     type="active"
                                     :source="data.item.listing.source">
                            </barcode>
                        </div>
                    </span>
                </template>

                <template #cell(inactive)="data">
                    <span>{{ inactiveCount(data.item) }}</span>

                    <span v-if="settings.showTagButtons && data.item.counts.inactive && data.item.counts.inactive.listingIndex" class="ml-2">
                        <b-button size="sm" variant="outline-secondary" @click="printTag(data.item.listing._id, data.item.counts.inactive.listingIndex)">
                            <b-icon-tag></b-icon-tag>
                        </b-button>

                        <div v-if="barcodeItem === data.item.listing._id" :id="'listingTag-' + data.item.listing._id + '-' + data.item.counts.inactive.listingIndex" :key="'barcode-' + data.item + data.item.counts.inactive.listingIndex" class="barcode-container">
                            <barcode :data="barcodeData(data.item, data.item.counts.inactive.listingIndex)"
                                     :text="barcodeData(data.item, data.item.counts.inactive.listingIndex)"
                                     :location="data.item.listing.location"
                                     type="inactive"
                                     :source="data.item.counts.inactive.source">
                            </barcode>
                        </div>
                    </span>
                </template>

                <template #cell(location)="data">
                    {{ data.item.listing.location }}
                </template>

                <template #cell(price)="data">
                    <span class="text-success">${{ data.item.listing.price }}</span>
                </template>


                <template #cell(grade)="data">
                    <span class='cellListItem d-inline-block' v-html="gradeData(data.item.listing.condition)"></span>
                </template>

                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong class="mx-3" v-if="creatingItem">Creating Inventory Item...</strong>
                        <strong class="mx-3" v-else>Loading...</strong>
                    </div>
                </template>

                <template #custom-foot="data">
                    <b-tr class="table-info">
                        <b-td colspan="6"></b-td>
                        <b-td class="text-secondary"><strong>{{ activeTotal(data) }}</strong></b-td>
                        <b-td class="text-secondary"><strong>{{ inactiveTotal(data) }}</strong></b-td>

                        <!-- <b-td align="right" class="text-secondary"><strong>Total:</strong></b-td> -->
                        <b-td class="text-secondary" colspan="2"><strong>${{ priceTotal(data) }}</strong></b-td>
                        <b-td></b-td>
                        <b-td></b-td>
                        <b-td></b-td>
                    </b-tr>
                </template>
            </b-table>
        </b-container>

    </div>
        <print-list :items="printItems" id="printList" class="print-list"></print-list>
    </div>
</template>

<script>
    import InventorySearch from "../components/inventory/InventorySearch";
    import searchMixin from "../mixins/search";
    import inventoryApi from "../api/inventory";
    import ordersApi from "../api/orders";
    import searchFilterNamesMixin from "../assets/searchFilterNames";
    import Barcode from "../components/Barcode";
    import PrintList from "../components/inventory/PrintList";
    import ItemMenu from "../components/inventory/ItemMenu";
    import utils from "../mixins/utils";
    import InventoryForm from '../containers/InventoryForm';
    import defaultInventoryItem from '../assets/defaultInventoryItem';
    import ObjectMixin from "../mixins/object";

    export default {
        name: "Inventory",
        components: { InventorySearch, Barcode, PrintList, ItemMenu, InventoryForm },
        mixins: [ searchMixin, inventoryApi, ordersApi, utils, searchFilterNamesMixin, defaultInventoryItem, ObjectMixin ],
        props: [ 'outsideQuery' ],
        data() {
            return {
                filters: {
                    noImagesAdmin: null,
                    hasImagesAdmin: null,
                    locationFrom: null,
                    locationTo: null,
                    added: null,
                    genre: null,
                    price: null,
                    offsite: null,
                    format: null,
                    size: null,
                    speed: null,
                    origin: null,
                    active: null,
                    inactive: null,
                    cover: null,
                    disc: null,
                    discCount: null,
                    integrationId: null,
                    orderDate: null,
                    orderDateTo: null,
                    noSalesSince: null,
                    platform: null,
                    adminGrade: null,
                    subject: null,
                    mediaAttrs: null,
                    pressingNotes: null,
                    noLocation: null,
                    noSource: null,
                    noPrice: null,
                    suppressed: null,
                    catalogNumber: null,
                    imageDateFrom: null,
                    imageDateTo: null,
                    inactiveUpdatedBy: null,
                    inactiveUpdated: null,
                    inactiveUpdatedTo: null,
                    integrationAddedBy: null,
                    integrationPlatform: null,
                    integrationAdded: null,
                    integrationAddedTo: null,
                },
                searchResults: {},
                numItems: 0,
                currentPage: 1,
                perPage: 100,
                items: [],
                selected: [],
                isBusy: false,
                searchQuery: {},
                fields: [
                    { key: 'itemId', label: 'Item', sortable: true },
                    { key: 'artist', label: 'Artist', sortable: true },
                    { key: 'title', label: 'Title', sortable: true },
                    { key: 'label', label: 'Label', sortable: true },
                    { key: 'genre', label: 'Genre'},
                    { key: 'format', label: 'Format' },
                    { key: 'active', label: 'Active', sortable: true },
                    { key: 'inactive', label: 'Inactive', sortable: true },
                    { key: 'price', label: '$', sortable: true, sortDirection: 'desc' },
                    { key: 'grade', label: 'Grade'},
                    { key: 'location', label: 'Location', sortable: true },
                    { key: 'pressing', label: 'Pressing' },
                    { key: 'media', label: 'Media' },
                ],
                barcodeItem: '',
                settings: {},
                printItems: [],
                showFilters: false,
                selectRange: false,
                isSaving: false,
                creatingItem: false,
                itemId: null,
                sortBy: '',
                sortDesc: false,
                templateItem: null,
                includeLabelUsers: ['craig', 'kj', 'edward', 'jason.nickle', 'tim'],
                itemFormKey: 0,
                pendingSearchItemId: null, // Store itemId to search for when modal closes
            }
        },
        created() {
            this.resetFilters();
        },
        mounted() {
            if (this.outsideQuery) {
                this.handleRoute();
            }
        },
        methods: {
            openItem(id) {
                this.itemId = id;
                this.itemFormKey += 1;
            },
            clearItemId() {   
                this.itemId = null;
                this.templateItem = null;
                this.showFilters = false;
                this.itemFormKey += 1;
                
                // If we have a pending search itemId, set it in the search query and search
                if (this.pendingSearchItemId) {
                    // Clear the itemIds filter since we're searching by query instead
                    const filters = { ...this.filters };
                    filters.itemIds = null;
                    filters.excludeItemIds = null;
                    
                    this.searchQuery.query = this.pendingSearchItemId;
                    this.filters = filters;
                    this.doSearch({ query: this.pendingSearchItemId, filters: filters });
                    this.pendingSearchItemId = null;
                }
            },
            switchToTemplate(templateItem) {
                // Switch to the new template item without closing the modal
                this.templateItem = templateItem;
                this.itemId = 'new';
                this.itemFormKey += 1;
            },
            handleItemSaved(newItemId) {
                // Update itemId when a new item is saved so Template button can work for the saved item
                this.itemId = newItemId;
                this.templateItem = null; // Clear template item since we now have a real item
            },
            removeFilterItem(filter) {
                this.removeFilter(filter);
                this.doSearch({ query: this.searchQuery?.query || null, filters: this.filters });
            },
            updateFiltersVisibility(showFilters) {
                this.showFilters = showFilters;
            },
            resetFilters() {
                this.filters = {
                    noImagesAdmin: false,
                    hasImagesAdmin: false,
                    locationFrom: null,
                    locationTo: null,
                    added: null,
                    genre: null,
                    price: null,
                    offsite: false,
                    format: null,
                    size: null,
                    speed: null,
                    origin: null,
                    active: null,
                    inactive: null,
                    cover: null,
                    disc: null,
                    discCount: null,
                    integrationId: null,
                    orderDate: null,
                    orderDateTo: null,
                    noSalesSince: null,
                    platform: null,
                    adminGrade: null,
                    subject: null,
                    mediaAttrs: null,
                    pressingNotes: null,
                    noLocation: null,
                    noSource: null,
                    noPrice: null,
                    suppressed: null,
                    catalogNumber: null,
                    itemIds: null,
                    excludeItemIds: null,
                    imageDateFrom: null,
                    imageDateTo: null,
                    inactiveUpdatedBy: null,
                    inactiveUpdated: null,
                    inactiveUpdatedTo: null,
                    integrationAddedBy: null,
                    integrationPlatform: null,
                    integrationAdded: null,
                    integrationAddedTo: null,
                }
            },
            async provider(ctx) {
                try {
                    const searchObj = this.buildSearchObject(this.searchQuery);
                        searchObj.page = ctx.currentPage;

                        if (this.sortBy && this.sortBy.length) {
                            searchObj.sort = this.sortBy;
                            searchObj.dir = this.sortDesc ? 'desc' : 'asc';
                        }

                        if (searchObj.catalogNumber && searchObj.catalogNumber.length && !searchObj.sort) {
                            searchObj.sort = 'catNo'
                        }

                        let results = await this.searchItems(searchObj);
                        this.numItems = results.totalDocs;
                        this.searchResults = results;
                        return this.parseSearchResults(results.docs);
                } catch (error) {
                    this.showError(error, 'Inventory Error');
                    console.log(error);
                    return [];
                }
            },
            parseSearchResults(results) {
                for (let i = 0; i < results.length; i++) {
                    if (results[i].listing.offsite) {
                        results[i]['_rowVariant'] = 'warning';
                    }
                }

                return results;
            },
            async doSearch(search) {
                if (!this.hasFilters) {
                    // make sure no lingering itemIds filter is in the filters
                    this.resetFilters();
                }

                if (!search.filters) {
                    search.filters = this.filters;
                }

                if (search.filters.orderDate || search.filters.platform || search.filters.orderSource || search.filters.orderAddedBy || search.filters.noSalesSince) {
                    this.isBusy = true;
                    // get items for order date from orders api
                    const searchObj = {
                        noSalesSince: search.filters.noSalesSince,
                        orderDate: search.filters.orderDate,
                        orderDateTo: search.filters.orderDateTo,
                        platform: search.filters.platform,
                        orderSource: search.filters.orderSource,
                        orderAddedBy: search.filters.orderAddedBy,
                    };

                    let itemIds = await this.getItemIdsFromOrders(searchObj);
                    if (!search.filters.noSalesSince) {
                        search.filters.excludeItemIds = null;
                        search.filters.itemIds = itemIds.join(',');
                    } else {
                        search.filters.itemIds = null,
                        search.filters.excludeItemIds = itemIds.join(',');
                    }
                    this.isBusy = false;
                }
                
                this.searchQuery = search || '';
        
                this.$nextTick(() => {
                    this.$refs.inventoryTable.refresh();
                });

            },
            async setFilters(filters) {
                this.filters = filters;
            },
            async getItemIdsFromOrders(search) {
                // theoretically we want all of the matching order items akin to order stats
                let query = {noLimit: true};

                if (search.orderDate) {
                    query.orderDate = search.orderDate;
                }

                if (search.orderDateTo) {
                    query.orderDateTo = search.orderDateTo;
                }

                if (!search.orderDate && !search.orderDateTo && !search.noSalesSince) {
                    // default to last year worth of order items
                    // need to make clear that user should use dates in conjunctions with other order options in inventory search
                    const oneYearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0];
                    const today = new Date().toISOString().split('T')[0];
                    query.orderDate = oneYearAgo;
                    query.orderDateTo = today;
                }

                if (search.noSalesSince) {
                    const today = new Date().toISOString().split('T')[0];
                    query.orderDate = search.noSalesSince;
                    query.orderDateTo = today;
                }

                if (search.platform) {
                    query.platform = search.platform;
                }

                if (search.orderSource) {
                    query.orderSource = search.orderSource;
                }

                if (search.orderAddedBy) {
                    query.orderAddedBy = search.orderAddedBy;
                }

                const orders = await this.getOrders(query);
                let itemIds = [];

                for (let i = 0; i < orders.length; i++) {
                    for (let k = 0; k < orders[i].items.length; k++) {
                        if (orders[i].items[k].details.itemId) {                            
                            if (!search.orderSource && !search.orderAddedBy) {
                                itemIds.push(orders[i].items[k].details.itemId);
                            }

                            let isAdded = false;

                            if (search.orderSource) {
                                if (search.orderSource === orders[i].items[k].details.listing.source) {
                                    itemIds.push(orders[i].items[k].details.itemId);
                                    isAdded = true;
                                }
                            } 
                            
                            if (search.orderAddedBy && !isAdded) {
                                if (search.orderAddedBy === orders[i].items[k].details.listing.username) {
                                    itemIds.push(orders[i].items[k].details.itemId);
                                }
                            } 
                        }
                    }
                }

                return itemIds;
            },
            rowSelected(rows) {
                this.selected = rows;
                this.printItems = this.selected;
            },
            setSelectRange() {
                this.selectRange = !this.selectRange;
            },
            async handleRoute() {
                this.resetFilters();
                this.searchQuery.query = this.outsideQuery.query;
                this.itemId = null;
                if (this.outsideQuery.filters) {
                    this.filters = Object.assign({}, this.filters, this.outsideQuery.filters)
                }

                await this.doSearch(this.outsideQuery);
                
                // Open item details if itemId is provided in outsideQuery
                if (this.outsideQuery.itemId) {
                    // Store the itemId to use in search when modal closes
                    this.pendingSearchItemId = this.outsideQuery.itemId;
                    this.$nextTick(() => {
                        this.openItem(this.outsideQuery.itemId);
                    });
                }
            },
            artistData(data) {
                return `${this.artist(data)}`;
            },
            titleData(data) {
                return `${this.title(data)}`;
            },
            release(data) {
                return `${this.artist(data)}: ${this.title(data)}`;
            },
            labelData(data) {
                return `${this.label(data)} ${this.catalogNo(data)}`;
            },
            pressingData(data) {
                return data.map(p => {
                    return p.text
                }).join(', ');
            },
            mediaData(data) {
                return data.map(media => {
                    return media.attributes;
                }).join(', ');
            },
            gradeData(conditionArray) {
                if (conditionArray.length < 1) return '';
                const sortOrder = ["item", "disc", "cassette", "cover", "case", "label", "insert"];
                const sortedArray = conditionArray.slice().sort((a, b) => sortOrder.indexOf(a.subject) - sortOrder.indexOf(b.subject));
                return sortedArray.map(condition => {return condition.grade}).join(', ');
            },
            format(data, separator = ', ') {
                if (!data.item.release.media.length || !data.item.release.media[0].size) {
                    return ``;
                }

                const formatMedia = (media) => {
                    let count = media.count || 1;
                    let countPrefix = count > 1 ? `${count}x` : '';

                    if (media.size === '12') {
                        if (media.duration === 'Single') {
                            return countPrefix + '12'
                        } else if (media.duration === 'LP') {
                            return countPrefix + 'LP';
                        } else {
                            return countPrefix + media.duration;
                        }
                    } else if (media.size === '10') {
                        if (media.speed === '78') {
                            return countPrefix + '78';
                        }
                        return countPrefix + '10';
                    } else if (media.size === '7') {
                        if (media.duration === 'EP') {
                            return countPrefix + 'EP'
                        } else {
                            return countPrefix + '45';
                        }
                    } else if (media.size === '5') {
                        return countPrefix + 'CD';
                    } else if (media.size === '4') {
                        return countPrefix + 'Cassette';
                    }

                    // Fallback for other sizes
                    let size = media.size;
                    if (count > 1) {
                        size = `${count}x${size}`;
                    }
                    return `${size}" ${media.duration || ''}`;
                };

                // Format all media elements and join them
                return data.item.release.media
                    .filter(media => media.size) // Only include media with size
                    .map(media => formatMedia(media))
                    .join(separator);
            },
            print() {
                this.$htmlToPaper('printList', { styles: [
                        'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
                        'https://unpkg.com/kidlat-css/css/kidlat.css',
                        './assets/itemPrint.css'
                    ] }, () => {
                });
            },
            artist(data) {
                if (!data.item.release.artist) {
                    return ``;
                }

                if (data.item.release.artist.first) {
                    return `${data.item.release.artist.first} ${data.item.release.artist.last}`.trim();
                }

                return `${data.item.release.artist.last}`.trim();
            },
            title(data) {
                if (data.item.release.titles && data.item.release.titles[0] && data.item.release.titles[1]) {
                    return `${data.item.release.titles[0]} / ${data.item.release.titles[1]}`;
                }

                return data.item.release.titles[0] || '';
            },
            label(data) {
                if (data.item.release.label && data.item.release.label.name) {
                    return `${data.item.release.label.name}`;
                }

                return '';
            },
            catalogNo(data) {
                if (data.item.release.label && data.item.release.label.catalogNo) {
                    return `${data.item.release.label.catalogNo}`;
                }

                return '';
            },
            inactiveCount(item) {
                if (!item.counts || !item.counts.inactive) {
                    return 0;
                }

                return item.counts.inactive.count;
            },
            barcodeData(item, index) {
                return item.itemId + '_' + (index + 1);
            },
            printTag(listingId, listingIndex) {
                this.barcodeItem = listingId;

                this.$nextTick(() => {
                    const printOptions = {
                        styles: [
                            'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
                            'https://unpkg.com/kidlat-css/css/kidlat.css',
                            './assets/barcodePrint.css'
                        ]
                    };

                    this.$htmlToPaper('listingTag-' + listingId + '-' + listingIndex, printOptions, () => {
                    });
                });
            },
            async addItem() {
                // this.creatingItem = true;
                // this.isBusy = true;
                // const item = await this.createItem();
                // this.creatingItem = false;
                // this.isBusy = false;
                this.templateItem = null;
                this.openItem('new');
            },
            async createTemplate() {
                if (this.selected.length !== 1) {
                    return;
                }
                
                const username = this.$auth.user.nickname;

                const selectedItem = this.selected[0];
                // Fetch the full item to ensure we have all the data
                const fullItem = await this.getItem(selectedItem.itemId);
                
                // Create template item with Artist, Title, Genre, and Media from selected item
                let defaultSource = localStorage.getItem('inventorySource') || null;
                const defaultLocation = localStorage.getItem('inventoryLocation') || null;
                if (username === 'jamin') {
                    defaultSource = null;
                }
                // Deep clone the default inventory item structure
                const templateItem = this.deepClone(this.defaultInventoryItem);
                
                // Copy Artist
                if (fullItem.release?.artist) {
                    templateItem.release.artist = {
                        first: fullItem.release.artist.first || '',
                        last: fullItem.release.artist.last || ''
                    };
                }
                
                // Copy Title
                if (fullItem.release?.titles && username !== 'jamin') {
                    templateItem.release.titles = [...fullItem.release.titles];
                }
                
                // Copy Genre
                if (fullItem.release?.genres) {
                    templateItem.release.genres = fullItem.release.genres.map(genre => ({
                        name: genre.name
                    }));
                }
                
                // Copy Media
                if (fullItem.release?.media) {
                    templateItem.release.media = fullItem.release.media.map(media => ({
                        count: media.count || 1,
                        attributes: media.attributes ? [...media.attributes] : [],
                        size: media.size || null,
                        duration: media.duration || null,
                        speed: media.speed || null
                    }));
                }
                
                // Apply defaults (location, source)
                const date = Date.now();
                templateItem.listing[0].username = this.$auth.user.nickname;
                templateItem.listing[0].date = date;
                if (defaultSource || defaultLocation) {
                    templateItem.listing[0].source = defaultSource;
                    templateItem.listing[0].location = defaultLocation;
                    templateItem.listing[1].location = defaultLocation;
                }
                
                // Clear notes/hype - don't copy from original item
                templateItem.notes = [];
                
                // Label: copy for includeLabelUsers, clear for others
                if (templateItem.release) {
                    if (this.includeLabelUsers.includes(username) && fullItem.release?.label) {
                        templateItem.release.label = {
                            name: fullItem.release.label.name || '',
                            catalogNo: fullItem.release.label.catalogNo || ''
                        };
                    } else {
                        templateItem.release.label = {
                            name: '',
                            catalogNo: ''
                        };
                    }
                }
                
                // Clear integration IDs - don't copy any integration IDs from original item
                if (templateItem.release?.integrations) {
                    templateItem.release.integrations = templateItem.release.integrations.map(integration => ({
                        platform: integration.platform
                    }));
                }
                
                this.templateItem = templateItem;
                console.log('templateItem', templateItem);
                this.openItem('new');
            },
            priceTotal(data) {
                let price = 0;

                data.items.forEach(item => {
                    if (!isNaN(item.listing.price)) {
                        price += item.listing.price;
                    }
                });

                return price.toLocaleString();
            },
            activeTotal(data) {
                let active = 0;

                data.items.forEach(item => {
                    if (item.listing.type === 'active' && !isNaN(item.listing.count)) {
                        active += item.listing.count;
                    }
                });

                return active;
            },
            inactiveTotal(data) {
                let inactive = 0;

                data.items.forEach(item => {
                    if (item.counts.inactive && !isNaN(item.counts.inactive.count)) {
                        inactive += item.counts.inactive.count;
                    }
                });

                return inactive;
            },
            filterName(filter) {
                return this.searchFilterNames[filter];
            },
        },
        computed: {
            hasFilters() {
                for (let key in this.filters) {
                    if ((key !== 'itemIds' && key !== 'excludeItemIds') && this.filters[key] !== false && this.filters[key] !== null) {
                        return true; // If any filter has a non-false and non-null value, return true
                    }
                }
                return false;
            },
        },
        watch: {
            outsideQuery(query) {
                if (query) {
                    this.handleRoute();
                }
            },
        }
    }
</script>

<style scoped>
    .barcode-container,
    .print-list {
        position: absolute;
        top: -9999px;
        left: -9999px;
    }

    .cellListItem {
        width: max-content;
        padding-right: 10px;
    }

    .overflow-auto {
        overflow: auto;
    }


</style>